import Helpers from "../../src/js/global/_helpers";

export default class FileUpload {
    constructor($module) {
        this.$module = $module;
        this.$button = $module.querySelector('.bc-file-upload__button');
        this.$input = $module.querySelector('input[type="file"]');
        this.$fileList = $module.querySelector(".bc-file-upload__list");
        this.$errorContainer = $module.querySelector(".bc-file-upload__error");
        this.$control = $module.querySelector(".bc-file-upload__control");
        this.$inputFiles = new DataTransfer();
        this.maxFileSize = parseInt(this.$input.getAttribute("data-max-size"), 10);
        this.maxFileCount = parseInt(this.$input.getAttribute("data-max-files"), 10)
        this.listItemTemplate = `
            <li class="bc-file-upload__list-item">
                <i class="bc-icon fa-regular fa-paperclip-vertical"></i>
                <span class="bc-heading--byline bc-file-upload__filename">
                    {{filename}}
                    <span class="bc-file-upload__size">({{filesize}})</span>
                </span>
                <button type="button" class="bc-file-upload__delete" data-filename="{{filename}}">Delete</button>
            </li>`;

        if (!this.$input || $module.classList.contains("is-enhanced")) {
            return;
        }

        this.initialize();
        this.setupEventListeners();
    }

    initialize() {
        this.$module.classList.add("is-enhanced");

        const id = this.generateGuid();
        const inputId = this.$input.id || `FileUpload-${id}`;
        const fileListId = this.$fileList.id || `FileUpload-${id}-FileList`;

        this.$input.id = inputId;
        this.$fileList.id = fileListId;

        this.setupAccessibility(inputId, fileListId);
    }

    setupAccessibility(inputId, fileListId) {
        Object.assign(this.$fileList, {
            role: "region",
            "aria-live": "polite",
            "aria-atomic": "true",
            "aria-relevant": "additions removals",
            "aria-controlledby": inputId
        });
        this.$input.setAttribute("aria-controls", fileListId);
    }

    generateGuid() {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    setupEventListeners() {
        const dragHandlers = {
            enter: () => this.$module.classList.add("is-over"),
            leave: () => this.$module.classList.remove("is-over"),
            over: (e) => e.preventDefault(),
            drop: (e) => {
                e.preventDefault();
                this.handleNewFiles(e.dataTransfer.files);
                this.$module.classList.remove("is-over");
                this.$input.files = this.$inputFiles.files;
            }
        };

        Object.entries(dragHandlers).forEach(([event, handler]) => {
            this.$control.addEventListener(`drag${event}`, handler);
        });

        this.$input.addEventListener("change", (e) => this.handleNewFiles(e.target.files));
        this.$fileList.addEventListener("click", this.handleDelete.bind(this));
        this.$button.addEventListener("click", (e) => {
            e.preventDefault();
            this.$input.click();
        });
    }

    handleNewFiles(newFiles) {
        if (!newFiles?.length) return;

        // Calculate how many more files we can add
        const remainingSlots = this.maxFileCount - this.$inputFiles.files.length;

        if (remainingSlots <= 0) {
            this.setErrors([`Maximum number of files (${this.maxFileCount}) exceeded. Please remove some files before adding more.`]);
            return;
        }

        // Take only the first N files that would fit within our limit
        const filesToAdd = Array.from(newFiles).slice(0, remainingSlots);

        filesToAdd.forEach(newFile => {
            if (!Array.from(this.$inputFiles.files).find(file => file.name === newFile.name)) {
                this.$inputFiles.items.add(newFile);
            }
        });

        this.updateFileList();
    }

    updateFileList() {
        this.clearError();

        if (!this.$inputFiles.files.length) {
            this.clearList();
            return;
        }

        const config = {
            maxSize: parseInt(this.$input.getAttribute("data-max-size"), 10),
            accept: this.$input.getAttribute("accept").split(","),
            messages: {
                size: this.$input.getAttribute("data-size-message"),
                type: this.$input.getAttribute("data-type-message"),
                name: this.$input.getAttribute("data-name-message")
            }
        };

        const allErrors = new Set();
        const validFiles = [];

        // Validate individual files
        Array.from(this.$inputFiles.files).forEach(file => {
            const validation = this.validateFile(file, config);
            if (validation.isValid) {
                validFiles.push(file);
            } else {
                validation.errors.forEach(error => allErrors.add(error));
            }
        });

        if (allErrors.size > 0) {
            this.setErrors(Array.from(allErrors));
        }

        this.validateAndTruncateFileName(validFiles);

        // Update DataTransfer object with valid files
        this.$inputFiles = new DataTransfer();
        validFiles.forEach(file => {
            this.$inputFiles.items.add(file);
        });
        this.$input.files = this.$inputFiles.files;

        // Clear and rebuild the file list
        this.clearList();
        validFiles.forEach(file => {
            this.addListItem(file.name, file.size);
        });
    }

    validateAndTruncateFileName(files) {
        const maxFileNameSize = Number.parseInt(this.$input.dataset.maxFileNameSize) || 0;

        if (maxFileNameSize <= 0) {
            return;
        }

        files.forEach((file, index) => {
            const fileNameParts = file.name.split('.');
            const fileExtension = fileNameParts.length > 1 ? fileNameParts.pop() : '';
            const fileName = fileNameParts.join('.');

            // Don't truncate file name if it doesn't exceed the maximum file name size set.
            if (fileName.length <= maxFileNameSize) {
                return;
            }

            const truncatedFileName = fileExtension
                ? `${fileName.substring(0, maxFileNameSize)}.${fileExtension}`
                : fileName.substring(0, maxFileNameSize);

            // Since file.name is readonly, we create a new instance with the new file name
            // and replace the old instance.
            files[index] = new File([file], truncatedFileName, {
                type: file.type,
                lastModified: file.lastModified,
            });
        });
    }

    validateFile(file, { accept, messages }) {
        const errors = [];

        if (!accept.includes(file.type) && !accept.includes("*/*")) {
            errors.push(messages.type || `File type not allowed: ${file.name}`);
        }

        if (this.maxFileSize && file.size > this.maxFileSize) {
            const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(1);
            errors.push(messages.size || `File size exceeds ${maxSizeMB}MB limit: ${file.name}`);
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    addListItem(filename, filesize) {
        const html = this.listItemTemplate
            .replace(/{{filename}}/g, filename)
            .replace(/{{filesize}}/g, this.formatFileSize(filesize));

        const template = document.createElement('template');
        template.innerHTML = html.trim();
        this.$fileList.appendChild(template.content.firstChild);
    }

    handleDelete(e) {
        if (!e.target.classList.contains("bc-file-upload__delete")) return;

        const filename = e.target.dataset.filename;
        e.target.closest(".bc-file-upload__list-item").remove();

        const fileIndex = Array.from(this.$inputFiles.files)
            .findIndex(file => file.name === filename);

        this.$inputFiles.items.remove(fileIndex);
        this.$input.files = this.$inputFiles.files;
    }

    formatFileSize(bytes) {
        const units = [
            { size: 1000000000, suffix: 'GB', precision: 3 },
            { size: 1000000, suffix: 'MB', precision: 2 },
            { size: 1000, suffix: 'KB', precision: 1 }
        ];

        const unit = units.find(unit => bytes >= unit.size) || { size: 1, suffix: 'B', precision: 0 };
        const size = (bytes / unit.size).toFixed(unit.precision);

        return `${size} ${unit.suffix}`;
    }

    clearList() {
        this.$fileList.textContent = "";
    }

    clearError() {
        this.$errorContainer.textContent = "";
        this.$control.classList.remove("bc-file-upload__control--error");
        this.$errorContainer.style.display = "none";
    }

    setErrors(errors) {
        this.$errorContainer.textContent = '';

        errors.forEach(error => {
            const errorItem = document.createElement('div');
            errorItem.className = 'bc-file-upload__error-item';

            const iconContainer = document.createElement('div');
            iconContainer.className = 'bc-file-upload__error-icon';

            const message = document.createElement('span');
            message.className = 'bc-file-upload__error-message';
            message.textContent = error;

            errorItem.appendChild(iconContainer);
            errorItem.appendChild(message);
            this.$errorContainer.appendChild(errorItem);
        });

        this.$control.classList.add("bc-file-upload__control--error");
        this.$errorContainer.style.display = "block";
    }
}